import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Send,
  FileText,
  Tag as TagIcon,
  ChevronDown,
  Search,
  Loader2,
  GitBranch,
  Building2,
  MoreVertical,
  Info,
  X,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { ChatContext } from '../types/chat';
import { CompanyContextModal } from './CompanyContextModal';
import { RebranchChatNameModal } from './RebranchChatNameModal';
import { OrganizationDetailsModal } from './OrganizationDetailsModal';
import { findSampleCompany } from '../lib/sampleCompanies';

function rowHasAllDocTags(rowTags: string[] | null | undefined, required: string[]): boolean {
  if (required.length === 0) return true;
  const set = new Set((rowTags || []).map((x) => (x || '').trim()).filter(Boolean));
  return required.every((r) => set.has(r.trim()));
}

function normalizeKbTags(tags: string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const t of tags) {
    const v = t.trim();
    if (!v || seen.has(v)) continue;
    seen.add(v);
    out.push(v);
  }
  return out;
}

type DocSearchHit = {
  id: string;
  title: string;
  state?: string;
  jurisdiction?: string;
};

/** Full clone: new title, panel context snapshot, composer draft; messages cloned in ChatPage. */
export type RebranchChatPayload = {
  title: string;
  draft: string;
  markdown_file_ids: string[];
  jurisdiction: string | null;
  state: string | null;
  kb_tags: string[];
  company_name: string | null;
  company_ein: string | null;
};

export interface SendMessageOptions {
  /** When set and non-empty, vector search is limited to these markdown files. Empty / omitted → all indexed docs. */
  markdownFileIds: string[] | null;
  jurisdiction: string | null;
  state: string | null;
  /** Document tags from `markdown_files.tags`; guides the model, does not filter vectors. */
  kbTags: string[];
  companyName: string | null;
  companyEin: string | null;
}

/** Exact JSON body last sent to the `chat` edge function (for debugging). */
export type ChatFunctionRequestPayload = {
  messages: Array<{ role: string; content: string }>;
  markdownFileIds: string[] | null;
  jurisdiction: string | null;
  state: string | null;
  kbTags: string[];
  companyName: string | null;
  companyEin: string | null;
};

interface PromptPanelProps {
  chatId: string | null;
  /** Title of the active chat (for default rebranch name). */
  activeChatTitle?: string | null;
  onSendMessage: (content: string, options: SendMessageOptions) => void;
  /** Clone all messages + context into a new named chat and navigate there. Rejects on failure. */
  onRebranchChat?: (payload: RebranchChatPayload) => Promise<void>;
  isLoading: boolean;
  /** Prior messages in this chat (count only); next send appends the new user message then calls the function. */
  conversationMessageCount: number;
  /** Last payload successfully sent to `functions.invoke('chat')`. */
  lastInvokePayload: ChatFunctionRequestPayload | null;
}

export function PromptPanel({
  chatId,
  activeChatTitle,
  onSendMessage,
  onRebranchChat,
  isLoading,
  conversationMessageCount,
  lastInvokePayload,
}: PromptPanelProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [fileTitleById, setFileTitleById] = useState<Record<string, string>>({});
  const [context, setContext] = useState<ChatContext | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [jurisdiction, setJurisdiction] = useState('');
  const [state, setState] = useState('');
  const [kbTags, setKbTags] = useState<string[]>([]);
  const [tagPickerNonce, setTagPickerNonce] = useState(0);
  const [companyName, setCompanyName] = useState('');
  const [companyEin, setCompanyEin] = useState('');
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [orgDetailsOpen, setOrgDetailsOpen] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  const [docSearch, setDocSearch] = useState('');
  const [docSearchHits, setDocSearchHits] = useState<DocSearchHit[]>([]);
  const [docSearchBusy, setDocSearchBusy] = useState(false);
  const [stateFacetOptions, setStateFacetOptions] = useState<
    { value: string; count: number }[]
  >([]);
  const [jurisdictionFacetOptions, setJurisdictionFacetOptions] = useState<
    { value: string; count: number }[]
  >([]);
  const [tagFacetOptions, setTagFacetOptions] = useState<{ value: string; count: number }[]>([]);
  const [localeFacetsLoading, setLocaleFacetsLoading] = useState(false);
  /** Narrows facet dropdowns to documents whose title matches this prefix (debounced). */
  const [localeFacetPretext, setLocaleFacetPretext] = useState('');
  const [debouncedLocaleFacetPretext, setDebouncedLocaleFacetPretext] = useState('');
  const [branchMenuOpen, setBranchMenuOpen] = useState(false);
  const [rebranchModalOpen, setRebranchModalOpen] = useState(false);
  const [rebranchBusy, setRebranchBusy] = useState(false);
  const branchMenuRef = useRef<HTMLDivElement>(null);

  const rebranchDefaultTitle = useMemo(() => {
    const base = activeChatTitle?.trim() || 'Chat';
    return `${base} (branch)`;
  }, [activeChatTitle]);

  /** Options for each dropdown; keep saved value visible if it is not in the current facet set. */
  const stateSelectOptions = useMemo(() => {
    const cur = state.trim();
    const base = [...stateFacetOptions];
    if (cur && !base.some((o) => o.value === cur)) {
      base.unshift({ value: cur, count: 0 });
    }
    return base;
  }, [state, stateFacetOptions]);

  const jurisdictionSelectOptions = useMemo(() => {
    const cur = jurisdiction.trim();
    const base = [...jurisdictionFacetOptions];
    if (cur && !base.some((o) => o.value === cur)) {
      base.unshift({ value: cur, count: 0 });
    }
    return base;
  }, [jurisdiction, jurisdictionFacetOptions]);

  const kbTagsKey = useMemo(() => kbTags.map((t) => t.trim()).filter(Boolean).join('\u0001'), [kbTags]);

  const tagAddSelectOptions = useMemo(() => {
    const picked = new Set(kbTags.map((t) => t.trim()).filter(Boolean));
    return tagFacetOptions.filter((o) => !picked.has(o.value));
  }, [tagFacetOptions, kbTagsKey]);

  const hitIds = useMemo(() => new Set(docSearchHits.map((h) => h.id)), [docSearchHits]);

  const selectedNotInHitList = useMemo(
    () => selectedFiles.filter((id) => !hitIds.has(id)),
    [selectedFiles, hitIds],
  );

  const nextSendPreview = useMemo(() => {
    const markdownFileIds =
      selectedFiles.length > 0
        ? selectedFiles.filter((id) => typeof id === 'string' && id.length > 0)
        : null;
    const selectedTitles = selectedFiles
      .map((id) => fileTitleById[id])
      .filter((t): t is string => Boolean(t && String(t).trim()));
    return {
      markdownFileIds,
      selectedDocumentTitles: selectedTitles.length ? selectedTitles : null,
      jurisdiction: jurisdiction.trim() || null,
      state: state.trim() || null,
      kbTags: normalizeKbTags(kbTags),
      companyName: companyName.trim() || null,
      companyEin: companyEin.trim() || null,
      draftUserMessage: input.trim() || null,
      priorMessagesInChat: conversationMessageCount,
      note:
        'Full `messages` array = all prior chat messages plus your draft as the latest user message (built in ChatPage).',
    };
  }, [
    selectedFiles,
    fileTitleById,
    input,
    jurisdiction,
    state,
    kbTags,
    companyName,
    companyEin,
    conversationMessageCount,
  ]);

  useEffect(() => {
    loadData();
  }, [chatId]);

  /** After rebranch, pre-fill the composer from navigation state. */
  useEffect(() => {
    const st = location.state as { rebranchDraft?: string; spinOffDraft?: string } | null;
    const draft = st?.rebranchDraft ?? st?.spinOffDraft;
    if (draft && typeof draft === 'string') {
      setInput(draft);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [chatId, location.pathname, location.state, navigate]);

  useEffect(() => {
    if (!branchMenuOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (branchMenuRef.current?.contains(e.target as Node)) return;
      setBranchMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [branchMenuOpen]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedLocaleFacetPretext(
        localeFacetPretext.replace(/%/g, '').replace(/_/g, '').trim().slice(0, 120),
      );
    }, 300);
    return () => window.clearTimeout(t);
  }, [localeFacetPretext]);

  /**
   * Facet options for state / jurisdiction / tags from `markdown_files` (title pretext narrows rows).
   */
  useEffect(() => {
    let cancelled = false;
    const j = jurisdiction.trim();
    const s = state.trim();
    const requiredTags = normalizeKbTags(kbTags);
    const facetQuery = debouncedLocaleFacetPretext;

    const load = async () => {
      setLocaleFacetsLoading(true);
      try {
        let sbQuery = supabase
          .from('markdown_files')
          .select('state, jurisdiction, title, tags')
          .eq('indexed', 'yes')
          .eq('algolia', 'yes');
        if (facetQuery.length > 0) {
          sbQuery = sbQuery.ilike('title', `%${facetQuery}%`);
        }
        const { data, error } = await sbQuery;

        if (cancelled) return;
        if (error || !data) {
          setStateFacetOptions([]);
          setJurisdictionFacetOptions([]);
          setTagFacetOptions([]);
          return;
        }

        type Row = {
          state: string | null;
          jurisdiction: string | null;
          title?: string | null;
          tags?: string[] | null;
        };

        const stateCounts = new Map<string, number>();
        const jurCounts = new Map<string, number>();
        const tagCounts = new Map<string, number>();

        for (const row of data as Row[]) {
          if (j && (row.jurisdiction || '').trim() !== j) continue;
          if (!rowHasAllDocTags(row.tags, requiredTags)) continue;
          const st = (row.state || '').trim();
          if (st) stateCounts.set(st, (stateCounts.get(st) || 0) + 1);
        }
        for (const row of data as Row[]) {
          if (s && (row.state || '').trim() !== s) continue;
          if (!rowHasAllDocTags(row.tags, requiredTags)) continue;
          const ju = (row.jurisdiction || '').trim();
          if (ju) jurCounts.set(ju, (jurCounts.get(ju) || 0) + 1);
        }
        for (const row of data as Row[]) {
          if (j && (row.jurisdiction || '').trim() !== j) continue;
          if (s && (row.state || '').trim() !== s) continue;
          for (const tag of row.tags || []) {
            const tv = (tag || '').trim();
            if (tv) tagCounts.set(tv, (tagCounts.get(tv) || 0) + 1);
          }
        }

        setStateFacetOptions(
          [...stateCounts.entries()]
            .map(([value, count]) => ({ value, count }))
            .sort((a, b) => a.value.localeCompare(b.value)),
        );
        setJurisdictionFacetOptions(
          [...jurCounts.entries()]
            .map(([value, count]) => ({ value, count }))
            .sort((a, b) => a.value.localeCompare(b.value)),
        );
        setTagFacetOptions(
          [...tagCounts.entries()]
            .map(([value, count]) => ({ value, count }))
            .sort((a, b) => a.value.localeCompare(b.value)),
        );
      } finally {
        if (!cancelled) setLocaleFacetsLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [jurisdiction, state, kbTagsKey, debouncedLocaleFacetPretext]);

  const loadData = async () => {
    setDocSearch('');
    setDocSearchHits([]);
    setLocaleFacetPretext('');
    setDebouncedLocaleFacetPretext('');

    if (chatId) {
      const { data: contextRes } = await supabase
        .from('chat_context')
        .select('*')
        .eq('chat_id', chatId)
        .maybeSingle();

      if (contextRes) {
        setContext(contextRes);
        setSelectedFiles(contextRes.markdown_file_ids || []);
        setJurisdiction(contextRes.jurisdiction || '');
        setState(contextRes.state || '');
        setKbTags(normalizeKbTags(((contextRes as ChatContext).kb_tags as string[] | undefined) ?? []));
        setCompanyName(contextRes.company_name || '');
        setCompanyEin(contextRes.company_ein || '');
      } else {
        setContext(null);
        setSelectedFiles([]);
        setJurisdiction('');
        setState('');
        setKbTags([]);
        setCompanyName('');
        setCompanyEin('');
        setFileTitleById({});
      }
    }
  };

  /** Resolve titles for documents in context (small queries; avoids loading hundreds of files). */
  useEffect(() => {
    if (selectedFiles.length === 0) {
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('markdown_files')
        .select('id, title')
        .in('id', selectedFiles);
      if (cancelled || error || !data) return;
      setFileTitleById((prev) => {
        const next = { ...prev };
        for (const row of data) {
          next[row.id] = (row.title as string)?.trim() || 'Untitled';
        }
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedFiles]);

  /** Debounced `markdown_files` document list for the picker; empty query = browse with locale filters. */
  useEffect(() => {
    const safe = docSearch.replace(/%/g, '').replace(/_/g, '').trim().slice(0, 120);

    setDocSearchBusy(true);
    const t = window.setTimeout(async () => {
      let sbQuery = supabase
        .from('markdown_files')
        .select('id, title, state, jurisdiction')
        .eq('indexed', 'yes')
        .eq('algolia', 'yes');
      if (safe.length > 0) {
        sbQuery = sbQuery.ilike('title', `%${safe}%`);
      }
      const jf = jurisdiction.trim();
      const sf = state.trim();
      const tnorm = normalizeKbTags(kbTags);
      if (sf) sbQuery = sbQuery.eq('state', sf);
      if (jf) sbQuery = sbQuery.eq('jurisdiction', jf);
      if (tnorm.length > 0) sbQuery = sbQuery.contains('tags', tnorm);
      const { data, error } = await sbQuery.order('title').limit(50);

      setDocSearchBusy(false);
      if (error || !data) {
        setDocSearchHits([]);
        return;
      }
      setDocSearchHits(
        data.map((row) => {
          const id = row.id as string;
          const title = ((row.title as string) || 'Untitled').trim() || 'Untitled';
          const st = ((row.state as string) || '').trim();
          const jur = ((row.jurisdiction as string) || '').trim();
          return {
            id,
            title,
            ...(st ? { state: st } : {}),
            ...(jur ? { jurisdiction: jur } : {}),
          };
        }),
      );
    }, 280);

    return () => {
      window.clearTimeout(t);
    };
  }, [docSearch, jurisdiction, state, kbTagsKey]);

  const setDocPickerChecked = useCallback((row: DocSearchHit, checked: boolean) => {
    if (checked) {
      setSelectedFiles((prev) => (prev.includes(row.id) ? prev : [...prev, row.id]));
      setFileTitleById((prev) => ({ ...prev, [row.id]: row.title }));
    } else {
      setSelectedFiles((prev) => prev.filter((x) => x !== row.id));
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading || !chatId) return;

    await saveContext();
    const markdownFileIds =
      selectedFiles.length > 0
        ? selectedFiles.filter((id) => typeof id === 'string' && id.length > 0)
        : null;
    const jurisdictionVal = jurisdiction.trim() || null;
    const stateVal = state.trim() || null;
    const kbTagsVal = normalizeKbTags(kbTags);
    const companyNameVal = companyName.trim() || null;
    const companyEinVal = companyEin.trim() || null;
    onSendMessage(input, {
      markdownFileIds,
      jurisdiction: jurisdictionVal,
      state: stateVal,
      kbTags: kbTagsVal,
      companyName: companyNameVal,
      companyEin: companyEinVal,
    });
    setInput('');
  };

  type ContextPatch = Partial<{
    markdown_file_ids: string[];
    jurisdiction: string | null;
    state: string | null;
    kb_tags: string[];
    company_name: string | null;
    company_ein: string | null;
    company_data: Record<string, unknown> | null;
  }>;

  const persistChatContext = async (patch: ContextPatch = {}) => {
    if (!chatId) return;

    const contextData = {
      chat_id: chatId,
      markdown_file_ids: patch.markdown_file_ids ?? selectedFiles,
      tag_ids: [] as string[],
      jurisdiction:
        patch.jurisdiction !== undefined ? patch.jurisdiction : jurisdiction.trim() || null,
      state: patch.state !== undefined ? patch.state : state.trim() || null,
      kb_tags: patch.kb_tags !== undefined ? patch.kb_tags : normalizeKbTags(kbTags),
      company_name:
        patch.company_name !== undefined ? patch.company_name : companyName.trim() || null,
      company_ein:
        patch.company_ein !== undefined ? patch.company_ein : companyEin.trim() || null,
      company_data:
        patch.company_data !== undefined
          ? patch.company_data
          : context?.company_data ?? null,
      updated_at: new Date().toISOString(),
    };

    if (context) {
      await supabase.from('chat_context').update(contextData).eq('id', context.id);
    } else {
      const { data: inserted } = await supabase
        .from('chat_context')
        .insert(contextData)
        .select()
        .single();
      if (inserted) setContext(inserted as ChatContext);
    }
  };

  const saveContext = () => persistChatContext();

  const handleJurisdictionSelect = (value: string) => {
    setJurisdiction(value);
    void persistChatContext({ jurisdiction: value.trim() || null });
  };

  const handleStateSelect = (value: string) => {
    setState(value);
    void persistChatContext({ state: value.trim() || null });
  };

  const addKbTagFromDropdown = (value: string) => {
    const v = value.trim();
    if (!v) return;
    setKbTags((prev) => {
      if (prev.includes(v)) return prev;
      const next = normalizeKbTags([...prev, v]);
      queueMicrotask(() => void persistChatContext({ kb_tags: next }));
      return next;
    });
    setTagPickerNonce((n) => n + 1);
  };

  const removeKbTag = (tag: string) => {
    setKbTags((prev) => {
      const next = prev.filter((t) => t !== tag);
      queueMicrotask(() => void persistChatContext({ kb_tags: normalizeKbTags(next) }));
      return next;
    });
  };

  const handleCompanyApply = (row: { name: string; ein: string } | null) => {
    if (!row) {
      setCompanyName('');
      setCompanyEin('');
      void persistChatContext({ company_name: null, company_ein: null, company_data: null });
    } else {
      setCompanyName(row.name);
      setCompanyEin(row.ein);
      const companyData = findSampleCompany(row.name, row.ein);
      void persistChatContext({
        company_name: row.name,
        company_ein: row.ein,
        company_data: companyData ? (companyData as unknown as Record<string, unknown>) : null,
      });
    }
  };

  const handleRebranchConfirm = async (title: string) => {
    if (!onRebranchChat || !chatId) return;
    setRebranchBusy(true);
    try {
      await onRebranchChat({
        title,
        draft: input,
        markdown_file_ids: [...selectedFiles],
        jurisdiction: jurisdiction.trim() || null,
        state: state.trim() || null,
        kb_tags: normalizeKbTags(kbTags),
        company_name: companyName.trim() || null,
        company_ein: companyEin.trim() || null,
      });
    } finally {
      setRebranchBusy(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 w-96 flex-col border-l border-r border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
      <div className="flex-shrink-0 border-b border-slate-200 p-4 dark:border-slate-800">
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Context & Prompt</h3>
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
        <div>
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="text-xs font-medium text-slate-600 dark:text-slate-400">
              Locale &amp; filters
            </span>
            {localeFacetsLoading ? (
              <span className="text-[11px] font-normal text-slate-400">Updating…</span>
            ) : null}
          </div>
          <div className="relative mb-3">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={localeFacetPretext}
              onChange={(e) => setLocaleFacetPretext(e.target.value)}
              placeholder="Filter by title…"
              autoComplete="off"
              className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-8 pr-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-transparent focus:ring-2 focus:ring-slate-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-2">
            <div className="min-w-0">
              <label className="mb-1.5 block text-[11px] font-medium text-slate-500 dark:text-slate-400">
                Jurisdiction
              </label>
              <select
                value={jurisdiction}
                onChange={(e) => handleJurisdictionSelect(e.target.value)}
                className="min-w-0 w-full appearance-none rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm text-slate-900 focus:border-transparent focus:ring-2 focus:ring-slate-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:focus:ring-slate-500"
              >
                <option value="">— None —</option>
                {jurisdictionSelectOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.count > 0 ? `${opt.value} (${opt.count})` : opt.value}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-0">
              <label className="mb-1.5 block text-[11px] font-medium text-slate-500 dark:text-slate-400">
                State
              </label>
              <select
                value={state}
                onChange={(e) => handleStateSelect(e.target.value)}
                className="min-w-0 w-full appearance-none rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm text-slate-900 focus:border-transparent focus:ring-2 focus:ring-slate-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:focus:ring-slate-500"
              >
                <option value="">— None —</option>
                {stateSelectOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.count > 0 ? `${opt.value} (${opt.count})` : opt.value}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mb-2 mt-6">
            <label className="mb-1.5 block text-[11px] font-medium text-slate-500 dark:text-slate-400">
              <TagIcon className="mr-1 inline h-3 w-3" />
              Tags
            </label>
            {kbTags.length > 0 && (
              <ul className="mb-2 space-y-1">
                {kbTags.map((tag) => (
                  <li
                    key={tag}
                    className="flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50/90 pl-2.5 dark:border-slate-600 dark:bg-slate-800/80"
                  >
                    <span className="min-w-0 flex-1 truncate py-1.5 text-xs text-slate-800 dark:text-slate-200" title={tag}>
                      {tag}
                    </span>
                    <button
                      type="button"
                      title={`Remove ${tag}`}
                      onClick={() => removeKbTag(tag)}
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-r-[7px] text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-900 dark:hover:bg-slate-700 dark:hover:text-slate-100"
                    >
                      <X className="h-4 w-4" aria-hidden />
                      <span className="sr-only">Remove tag</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <select
              key={tagPickerNonce}
              defaultValue=""
              onChange={(e) => {
                const v = e.target.value;
                if (v) addKbTagFromDropdown(v);
              }}
              className="min-w-0 w-full appearance-none rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm text-slate-900 focus:border-transparent focus:ring-2 focus:ring-slate-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:focus:ring-slate-500"
            >
              <option value="">Add tag…</option>
              {tagAddSelectOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.count > 0 ? `${opt.value} (${opt.count})` : opt.value}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="mb-2 block text-xs font-medium text-slate-600 dark:text-slate-400">
            <Building2 className="mr-1 inline h-3 w-3" />
            Organization
          </label>
          <div className="flex gap-2 items-stretch">
            <button
              type="button"
              onClick={() => setCompanyModalOpen(true)}
              disabled={!chatId}
              className="flex min-w-0 flex-1 items-start justify-start gap-2 rounded-lg border border-slate-200 px-3 py-2 text-left text-sm font-medium text-slate-800 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:text-slate-100 dark:hover:bg-slate-800"
            >
              <Building2 className="w-4 h-4 shrink-0 text-slate-600 mt-0.5" />
              <span className="min-w-0 flex flex-col">
                {companyName.trim() ? (
                  <>
                    <span className="text-sm font-medium leading-snug">{companyName.trim()}</span>
                    <span className="text-[11px] text-slate-500 font-mono leading-snug mt-0.5">
                      EIN {companyEin.trim() || 'N/A'}
                    </span>
                    {(() => {
                      const company = findSampleCompany(companyName, companyEin);
                      return company ? (
                        <span className="text-[11px] text-slate-500 leading-snug">
                          {company.state}
                        </span>
                      ) : null;
                    })()}
                  </>
                ) : (
                  <span className="leading-snug">Select organization…</span>
                )}
              </span>
            </button>
            {companyName.trim() ? (
              <>
                <button
                  type="button"
                  onClick={() => setOrgDetailsOpen(true)}
                  disabled={!chatId}
                  title="View organization details (demo data)"
                  className="flex w-10 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                >
                  <Info className="w-4 h-4" aria-hidden />
                  <span className="sr-only">Organization details</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setOrgDetailsOpen(false);
                    setCompanyModalOpen(false);
                    handleCompanyApply(null);
                  }}
                  disabled={!chatId}
                  title="Clear organization"
                  className="flex w-10 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-100"
                >
                  <X className="h-4 w-4" aria-hidden />
                  <span className="sr-only">Clear organization</span>
                </button>
              </>
            ) : null}
          </div>
        </div>

        <div>
          <label className="mb-2 block text-xs font-medium text-slate-600 dark:text-slate-400">
            <FileText className="mr-1 inline h-3 w-3" />
            Documents ({selectedFiles.length} in context)
          </label>

          <div className="relative mb-2">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={docSearch}
              onChange={(e) => setDocSearch(e.target.value)}
              placeholder="Filter list by title (optional)…"
              autoComplete="off"
              className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-8 pr-9 text-sm text-slate-900 placeholder:text-slate-400 focus:border-transparent focus:ring-2 focus:ring-slate-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500"
            />
            {docSearchBusy && (
              <Loader2 className="absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-400" />
            )}
          </div>

          <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800/80">
            {docSearchBusy && docSearchHits.length === 0 && selectedNotInHitList.length === 0 && (
              <p className="px-3 py-6 text-center text-xs text-slate-400 dark:text-slate-500">Loading documents…</p>
            )}
            {!docSearchBusy && docSearchHits.length === 0 && selectedNotInHitList.length === 0 && (
              <p className="px-3 py-6 text-center text-xs text-slate-400 dark:text-slate-500">
                No documents match the current filters and search.
              </p>
            )}
            {docSearchHits.map((row) => {
              const checked = selectedFiles.includes(row.id);
              return (
                <label
                  key={row.id}
                  className="flex cursor-pointer items-start gap-2 border-b border-slate-100 px-3 py-2 last:border-0 hover:bg-slate-50 dark:border-slate-700/80 dark:hover:bg-slate-800"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => setDocPickerChecked(row, e.target.checked)}
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-slate-300 text-slate-800 focus:ring-slate-500 dark:border-slate-600 dark:bg-slate-800"
                  />
                  <span className="min-w-0 flex-1">
                    <span
                      className="block truncate text-xs font-medium text-slate-800 dark:text-slate-200"
                      title={row.title}
                    >
                      {row.title}
                    </span>
                    {(row.state || row.jurisdiction) && (
                      <span className="mt-0.5 block truncate text-[11px] text-slate-500 dark:text-slate-400">
                        {[row.state, row.jurisdiction].filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
            {selectedNotInHitList.length > 0 && (
              <>
                <div className="sticky top-0 z-[1] border-b border-slate-200 bg-slate-100/95 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 backdrop-blur-sm dark:border-slate-600 dark:bg-slate-900/95 dark:text-slate-400">
                  Also in context (not in list above)
                </div>
                {selectedNotInHitList.map((id) => (
                  <label
                    key={id}
                    className="flex cursor-pointer items-center gap-2 border-b border-slate-100 px-3 py-2 last:border-0 hover:bg-slate-50 dark:border-slate-700/80 dark:hover:bg-slate-800"
                  >
                    <input
                      type="checkbox"
                      checked
                      onChange={() =>
                        setDocPickerChecked(
                          { id, title: fileTitleById[id] ?? 'Untitled' },
                          false,
                        )
                      }
                      className="h-3.5 w-3.5 shrink-0 rounded border-slate-300 text-slate-800 focus:ring-slate-500 dark:border-slate-600 dark:bg-slate-800"
                    />
                    <span
                      className="min-w-0 flex-1 truncate text-xs text-slate-700 dark:text-slate-300"
                      title={fileTitleById[id]}
                    >
                      {fileTitleById[id] ?? 'Loading…'}
                    </span>
                  </label>
                ))}
              </>
            )}
          </div>
        </div>

        <div className="mt-1 border-t border-slate-200 pt-2 dark:border-slate-800">
          <button
            type="button"
            onClick={() => setDebugOpen((o) => !o)}
            className="-mx-1 flex w-full items-center justify-between gap-2 rounded-lg py-1 px-1 text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            <span className="text-xs font-medium text-slate-600 dark:text-slate-400">
              Chat request (debug JSON)
            </span>
            <ChevronDown
              className={`h-4 w-4 shrink-0 text-slate-500 transition-transform dark:text-slate-400 ${debugOpen ? 'rotate-180' : ''}`}
            />
          </button>
          {debugOpen && (
            <pre className="mt-2 max-h-[min(55vh,28rem)] overflow-y-auto whitespace-pre-wrap break-words rounded-lg border border-slate-200 bg-slate-50 p-3 font-mono text-xs leading-relaxed text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
              {JSON.stringify(
                {
                  _note:
                    'lastInvoke is the body sent to functions.invoke("chat"); the edge function may prepend a RAG system message before OpenAI.',
                  nextSendPreview,
                  lastInvoke: lastInvokePayload,
                },
                null,
                2,
              )}
            </pre>
          )}
        </div>
      </div>

      <div className="flex-shrink-0 border-t border-slate-200 p-4 dark:border-slate-800">
        <form onSubmit={handleSubmit} className="space-y-3">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Send a message..."
            disabled={isLoading || !chatId}
            rows={6}
            className="w-full resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-transparent focus:ring-2 focus:ring-slate-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
          />

          {isLoading && (
            <div
              className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-800/80 dark:text-slate-400"
              role="status"
              aria-live="polite"
            >
              <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-slate-500 dark:text-slate-400" aria-hidden />
              <span>Waiting for assistant…</span>
            </div>
          )}

          <div className="flex items-stretch gap-2">
            <button
              type="submit"
              disabled={isLoading || rebranchBusy || !input.trim() || !chatId}
              className="flex min-w-0 flex-1 items-center justify-center gap-2 rounded-lg bg-slate-800 px-4 py-2.5 text-white transition-colors hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-slate-200 dark:text-slate-900 dark:hover:bg-white"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
              ) : (
                <Send className="h-4 w-4 shrink-0" aria-hidden />
              )}
              <span className="text-sm font-medium">Send Message</span>
            </button>
            {onRebranchChat && (
              <div className="relative flex-shrink-0" ref={branchMenuRef}>
                <button
                  type="button"
                  disabled={isLoading || rebranchBusy || !chatId}
                  onClick={() => setBranchMenuOpen((o) => !o)}
                  title="More actions"
                  className="flex h-full min-h-[42px] items-center justify-center rounded-lg border border-slate-200 bg-white px-2.5 text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                >
                  {rebranchBusy ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <MoreVertical className="w-4 h-4" />
                  )}
                </button>
                {branchMenuOpen && (
                  <div className="absolute bottom-full right-0 z-20 mb-1 w-52 rounded-lg border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-900">
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50"
                      onClick={() => {
                        setBranchMenuOpen(false);
                        setRebranchModalOpen(true);
                      }}
                    >
                      <GitBranch className="h-4 w-4 shrink-0 text-slate-500" />
                      Rebranch chat
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </form>
      </div>

      {rebranchModalOpen && (
        <RebranchChatNameModal
          defaultTitle={rebranchDefaultTitle}
          onConfirm={handleRebranchConfirm}
          onClose={() => setRebranchModalOpen(false)}
        />
      )}

      <OrganizationDetailsModal
        open={orgDetailsOpen}
        onClose={() => setOrgDetailsOpen(false)}
        profile={findSampleCompany(companyName, companyEin) ?? null}
        fallbackName={companyName}
        fallbackEin={companyEin}
      />

      <CompanyContextModal
        open={companyModalOpen}
        onClose={() => setCompanyModalOpen(false)}
        selected={
          companyName.trim()
            ? { name: companyName.trim(), ein: companyEin.trim() }
            : null
        }
        onApply={handleCompanyApply}
      />

    </div>
  );
}
